import { injectable, inject } from 'inversify';
import config from '@framework/config';
import CookieRepository from '@framework/browser/storage/CookieRepository';
import ClientReqData from '@framework/router/Next/headers/ClientReqData';
import formatUuid from '@framework/data-helpers/formatters/formatUuid';
import Fingerprint from '@fingerprintjs/fingerprintjs';
import isServer from '@framework/router/Next/isServer';
import Container from '@framework/di/Container';
import TrackingConsent from '@framework/cookie-consent/TrackingConsent';
import { ConsentCategory } from '@framework/cookie-consent/constants';

@injectable()
class DeviceInfoService {
  constructor(
    @inject(CookieRepository) private readonly cookieRepo: CookieRepository,
    @inject(Container) private readonly container: Container,
    @inject(TrackingConsent) private readonly trackingConsent: TrackingConsent,
  ) {}

// @ts-ignore TODO: FIN-32798 strictNullChecks
  private userAgent: string;

  private fingerprint?: Promise<string>;

  init({ fingerprint }: { fingerprint: string }) {
    if (fingerprint) {
      this.fingerprint = Promise.resolve(fingerprint);
      this.storeFingerprint(fingerprint);
    }
  }

  getUserAgent = async () => {
    if (!this.userAgent) {
      const clientReqData = await this.container.getAsync(ClientReqData);
      this.userAgent = clientReqData.userAgent;
    }
    return this.userAgent;
  };

  getFingerprint = async (): Promise<string> => {
    if (!isServer() && !this.trackingConsent.hasConsent(ConsentCategory.Functional)) {
      return '';
    }

    if (!this.fingerprint) {
      this.fingerprint = this.getStoredOrCalculatedFingerprint().catch((error: unknown) => {
        this.fingerprint = undefined;
        throw error;
      });
    }

    return this.fingerprint;
  };

  private getStoredOrCalculatedFingerprint = async (): Promise<string> => {
    const storedFingerprint = await this.cookieRepo.get('fingerprint');

    if (storedFingerprint) {
      return storedFingerprint;
    }

    const fingerprint = formatUuid.addDashes(await this.calculateFingerprint());
    this.storeFingerprint(fingerprint);

    return fingerprint;
  };

  private storeFingerprint = (fingerprint: string): void => {
    const isFinsightHost = isServer() ||
      window.location.hostname === config.host ||
      window.location.hostname.endsWith(`.${ config.host }`);

    this.cookieRepo.set('fingerprint', fingerprint, {
      domain: isFinsightHost ? `.${ config.host }` : undefined,
      path: '/',
    });
  };

  private calculateFingerprint = async () => {
    if (isServer()) {
      return 'server-request-fingerprint-needs-fix';
    }

    const fp = await Fingerprint.load({ monitoring: false });
    const result = await fp.get();

    return Fingerprint.hashComponents(result.components);
  };
}

export default DeviceInfoService;
